import { parsePhoneNumber } from 'libphonenumber-js'
import {
  DocusignFieldTabLabel,
  DocusignVehicleType,
  TaxClassificationType,
} from 'src/constants/docusign'
import { DocusignSigningField } from 'src/interfaces/docusign'
import * as Yup from 'yup'

Yup.addMethod(Yup.string, 'validatePhone', function (errorMessage) {
  return this.test(`test-phone`, errorMessage, function (value) {
    const { path, createError } = this

    if (!value || value.length < 10) {
      return createError({ path, message: errorMessage })
    }

    try {
      const parsedPhoneNumber = parsePhoneNumber(value, 'US')

      return (
        !!parsedPhoneNumber?.formatInternational() || createError({ path, message: errorMessage })
      )
    } catch (err) {
      return createError({ path, message: errorMessage })
    }
  })
})

const validationMap = {
  [DocusignFieldTabLabel.phoneNumber]: Yup.string()
    // @ts-ignore
    .validatePhone('Phone number is invalid'),
  [DocusignFieldTabLabel.phoneNumberHome]: Yup.string()
    // @ts-ignore
    .validatePhone('Home phone number is invalid'),
  [DocusignFieldTabLabel.email]: Yup.string().email('Email is invalid'),
  [DocusignFieldTabLabel.fullName]: Yup.string(),
  [DocusignFieldTabLabel.firstName]: Yup.string(),
  [DocusignFieldTabLabel.lastName]: Yup.string(),
  [DocusignFieldTabLabel.mi]: Yup.string(),
  [DocusignFieldTabLabel.driverNumber]: Yup.string(),
  [DocusignFieldTabLabel.address]: Yup.string(),
  [DocusignFieldTabLabel.city]: Yup.string(),
  [DocusignFieldTabLabel.state]: Yup.string(),
  [DocusignFieldTabLabel.zipcode]: Yup.string().length(5, 'ZIP code is invalid'),
  [DocusignFieldTabLabel.county]: Yup.string(),
  [DocusignFieldTabLabel.cityStateZipcode]: Yup.string(),
  [DocusignFieldTabLabel.prevAddress1]: Yup.string(),
  [DocusignFieldTabLabel.prevCity1]: Yup.string(),
  [DocusignFieldTabLabel.prevState1]: Yup.string(),
  [DocusignFieldTabLabel.prevZipcode1]: Yup.string().length(5, 'ZIP code is invalid'),
  [DocusignFieldTabLabel.prevAddress2]: Yup.string(),
  [DocusignFieldTabLabel.prevCity2]: Yup.string(),
  [DocusignFieldTabLabel.prevState2]: Yup.string(),
  [DocusignFieldTabLabel.prevZipcode2]: Yup.string().length(5, 'ZIP code is invalid'),
  [DocusignFieldTabLabel.ssn]: Yup.string().length(11, 'Social security number is invalid'),
  [DocusignFieldTabLabel.ssnLast4]: Yup.string().length(
    4,
    'Social security number last 4 digits is invalid',
  ),
  [DocusignFieldTabLabel.tin]: Yup.string().length(11, 'Taxpayer ID number is invalid'),
  [DocusignFieldTabLabel.fein]: Yup.string().length(10, 'Federal employer ID number is invalid'),
  [DocusignFieldTabLabel.dob]: Yup.string().length(10, 'Date of birth is invalid'),
  [DocusignFieldTabLabel.beneficiary]: Yup.string(),
  [DocusignFieldTabLabel.beneficiaryRelationship]: Yup.string(),
  [DocusignFieldTabLabel.beneficiaryFirstName]: Yup.string(),
  [DocusignFieldTabLabel.beneficiaryLastName]: Yup.string(),
  [DocusignFieldTabLabel.beneficiaryMI]: Yup.string(),
  [DocusignFieldTabLabel.beneficiaryAddress]: Yup.string(),
  [DocusignFieldTabLabel.beneficiaryDeathToEstateCheckbox]: Yup.boolean().when(
    [
      DocusignFieldTabLabel.beneficiaryRelationship,
      DocusignFieldTabLabel.beneficiaryAddress,
      DocusignFieldTabLabel.beneficiaryFirstName,
      DocusignFieldTabLabel.beneficiaryLastName,
    ],
    {
      is: (relationship: string, address: string, firstName: string, lastName: string) =>
        !relationship || !address || !firstName || !lastName,
      then: (schema) =>
        schema.oneOf(
          [true],
          'Press checkbox if you want the benefit to go to your estate or enter beneficiary above',
        ),
    },
  ),
  [DocusignFieldTabLabel.insuranceCarrier]: Yup.string(),
  [DocusignFieldTabLabel.otherInsurancePlan]: Yup.string().required(
    'Please chose other insurance plan option',
  ),
  [DocusignFieldTabLabel.eSignatureAck]: Yup.boolean().oneOf(
    [true],
    'Electronic signature acknowledgement is required',
  ),
  [DocusignFieldTabLabel.businessName]: Yup.string(),
  [DocusignFieldTabLabel.ein]: Yup.string().length(
    10,
    'Employer Identification Number (EIN) is invalid',
  ),
  [DocusignFieldTabLabel.vehicleYear]: Yup.string().length(4, 'Vehicle Year is invalid'),
  [DocusignFieldTabLabel.vehicleMake]: Yup.string(),
  [DocusignFieldTabLabel.vehicleModel]: Yup.string(),
  [DocusignFieldTabLabel.vehicleVIN]: Yup.string().length(17, 'Vehicle VIN is invalid'),
  [DocusignFieldTabLabel.vehicleLicensePlate]: Yup.string(),
  [DocusignFieldTabLabel.gvw]: Yup.string(),
  [DocusignFieldTabLabel.vehicleType]: Yup.string(),
  [DocusignFieldTabLabel.vehicleTypeOther]: Yup.string().when(DocusignFieldTabLabel.vehicleType, {
    is: DocusignVehicleType.Other,
    then: (schema) => schema.required('Vehicle type is required'),
  }),
  [DocusignFieldTabLabel.vehicleTypeRequired]: Yup.string(),
  [DocusignFieldTabLabel.vehicleTypeOtherRequired]: Yup.string().when(
    DocusignFieldTabLabel.vehicleTypeRequired,
    {
      is: DocusignVehicleType.Other,
      then: (schema) => schema.required('Vehicle type is required'),
    },
  ),
  [DocusignFieldTabLabel.vehicleYear2]: Yup.string().length(4, 'Vehicle Year is invalid'),
  [DocusignFieldTabLabel.vehicleMake2]: Yup.string(),
  [DocusignFieldTabLabel.vehicleModel2]: Yup.string(),
  [DocusignFieldTabLabel.vehicleVIN2]: Yup.string().length(17, 'Vehicle VIN is invalid'),
  [DocusignFieldTabLabel.vehicleLicensePlate2]: Yup.string(),
  [DocusignFieldTabLabel.gvw2]: Yup.string(),
  [DocusignFieldTabLabel.vehicleType2]: Yup.string(),
  [DocusignFieldTabLabel.vehicleTypeOther2]: Yup.string().when(DocusignFieldTabLabel.vehicleType2, {
    is: DocusignVehicleType.Other,
    then: (schema) => schema.required('Vehicle type is required'),
  }),
  [DocusignFieldTabLabel.vehicleYear3]: Yup.string().length(4, 'Vehicle Year is invalid'),
  [DocusignFieldTabLabel.vehicleMake3]: Yup.string(),
  [DocusignFieldTabLabel.vehicleModel3]: Yup.string(),
  [DocusignFieldTabLabel.vehicleVIN3]: Yup.string().length(17, 'Vehicle VIN is invalid'),
  [DocusignFieldTabLabel.vehicleLicensePlate3]: Yup.string(),
  [DocusignFieldTabLabel.gvw3]: Yup.string(),
  [DocusignFieldTabLabel.vehicleType3]: Yup.string(),
  [DocusignFieldTabLabel.vehicleTypeOther3]: Yup.string().when(DocusignFieldTabLabel.vehicleType3, {
    is: DocusignVehicleType.Other,
    then: (schema) => schema.required('Vehicle type is required'),
  }),
  [DocusignFieldTabLabel.taxClassificationType]: Yup.string().required(
    'Tax classification type is required',
  ),
  [DocusignFieldTabLabel.llcType]: Yup.string().when(DocusignFieldTabLabel.taxClassificationType, {
    is: TaxClassificationType.Llc,
    then: (schema) => schema.required('LLC type is required'),
  }),
  [DocusignFieldTabLabel.exemptPayeeCode]: Yup.string(),
  [DocusignFieldTabLabel.exemptionFatcaCode]: Yup.string(),
  [DocusignFieldTabLabel.mmsOregonAddress]: Yup.string(),
  [DocusignFieldTabLabel.driverLicenseNumber]: Yup.string(),
  [DocusignFieldTabLabel.driverLicenseState]: Yup.string(),
  [DocusignFieldTabLabel.otherForeignPartners]: Yup.boolean(),
  [DocusignFieldTabLabel.otherTaxClassification]: Yup.string().when(
    DocusignFieldTabLabel.taxClassificationType,
    {
      is: TaxClassificationType.Other,
      then: (schema) => schema.required('Other tax classification is required'),
    },
  ),
  [DocusignFieldTabLabel.inselAgreementCheckbox]: Yup.boolean().oneOf(
    [true],
    'Acceptance of Membership Agreement is required',
  ),
  [DocusignFieldTabLabel.taxId]: Yup.string(),
  [DocusignFieldTabLabel.companyTitle]: Yup.string(),
  [DocusignFieldTabLabel.smartDeliveryCheckboxLease]: Yup.boolean().oneOf(
    [true],
    'Acknowledgement is required',
  ),
  [DocusignFieldTabLabel.smartDeliveryCheckboxAdAck]: Yup.boolean(),
  [DocusignFieldTabLabel.smartDeliveryCheckboxShirt]: Yup.boolean(),
  [DocusignFieldTabLabel.smartDeliveryCheckboxIdBadge]: Yup.boolean(),
  [DocusignFieldTabLabel.smartDeliveryCheckboxDecal]: Yup.boolean(),
  [DocusignFieldTabLabel.addressSinceDate]: Yup.string().length(7, 'Enter date (MM/YYYY)'),
  [DocusignFieldTabLabel.prevAddressSinceDate1]: Yup.string().length(7, 'Enter date (MM/YYYY)'),
  [DocusignFieldTabLabel.prevAddressSinceDate2]: Yup.string().length(7, 'Enter date (MM/YYYY)'),
  [DocusignFieldTabLabel.w9FullName]: Yup.string().when(DocusignFieldTabLabel.w9BusinessName, {
    is: (w9BusinessName: string) => !w9BusinessName,
    then: (schema) => schema.required('Individual name or business name is required'),
  }),
  [DocusignFieldTabLabel.w9BusinessName]: Yup.string().when(DocusignFieldTabLabel.w9FullName, {
    is: (w9FullName: string) => !w9FullName,
    then: (schema) => schema.required('Individual name or business name is required'),
  }),
  [DocusignFieldTabLabel.w9Address]: Yup.string(),
  [DocusignFieldTabLabel.w9City]: Yup.string(),
  [DocusignFieldTabLabel.w9State]: Yup.string(),
  [DocusignFieldTabLabel.w9County]: Yup.string(),
  [DocusignFieldTabLabel.w9Zipcode]: Yup.string().length(5, 'ZIP code is invalid'),
  [DocusignFieldTabLabel.w9Ssn]: Yup.string()
    .length(11, 'Social security number is invalid')
    .when(DocusignFieldTabLabel.w9Ein, {
      is: (w9Ein: string) => !w9Ein,
      then: (schema) =>
        schema.required('Social security number or employer identification number is required'),
    }),
  [DocusignFieldTabLabel.w9Ein]: Yup.string()
    .length(10, 'Employer Identification Number (EIN) is invalid')
    .when(DocusignFieldTabLabel.w9Ssn, {
      is: (w9Ssn: string) => !w9Ssn,
      then: (schema) =>
        schema.required('Social security number or employer identification number is required'),
    }),
  [DocusignFieldTabLabel.bankName]: Yup.string(),
  [DocusignFieldTabLabel.bankRoutingNumber]: Yup.string().length(9, 'Routing number is invalid'),
  [DocusignFieldTabLabel.bankAccountNumber]: Yup.string()
    .min(8, 'Account number is invalid')
    .max(17, 'Account number is invalid'),
  [DocusignFieldTabLabel.bankAccountType]: Yup.string().required('Account type is required'),
}

const requiredMap = {
  [DocusignFieldTabLabel.phoneNumber]: 'Phone number is required',
  [DocusignFieldTabLabel.phoneNumberHome]: 'Home phone number is required',
  [DocusignFieldTabLabel.email]: 'Email address is required',
  [DocusignFieldTabLabel.fullName]: 'Full name is required',
  [DocusignFieldTabLabel.firstName]: 'First name is required',
  [DocusignFieldTabLabel.lastName]: 'Last name is required',
  [DocusignFieldTabLabel.mi]: 'Middle initial is required',
  [DocusignFieldTabLabel.driverNumber]: 'Driver number is required',
  [DocusignFieldTabLabel.address]: 'Address is required',
  [DocusignFieldTabLabel.city]: 'City is required',
  [DocusignFieldTabLabel.state]: 'State is required',
  [DocusignFieldTabLabel.zipcode]: 'ZIP code is required',
  [DocusignFieldTabLabel.county]: 'County is required',
  [DocusignFieldTabLabel.cityStateZipcode]: 'City, state, and ZIP code are required',
  [DocusignFieldTabLabel.prevAddress1]: 'Address is required',
  [DocusignFieldTabLabel.prevCity1]: 'City is required',
  [DocusignFieldTabLabel.prevState1]: 'State is required',
  [DocusignFieldTabLabel.prevZipcode1]: 'ZIP code is required',
  [DocusignFieldTabLabel.prevAddress2]: 'Address is required',
  [DocusignFieldTabLabel.prevCity2]: 'City is required',
  [DocusignFieldTabLabel.prevState2]: 'State is required',
  [DocusignFieldTabLabel.prevZipcode2]: 'ZIP code is required',
  [DocusignFieldTabLabel.ssn]: 'Social security number is required',
  [DocusignFieldTabLabel.ssnLast4]: 'Social security number is required',
  [DocusignFieldTabLabel.tin]: 'Taxpayer ID number is required',
  [DocusignFieldTabLabel.fein]: 'Federal employer ID number is required',
  [DocusignFieldTabLabel.dob]: 'Date of birth is required',
  [DocusignFieldTabLabel.beneficiary]: 'Beneficiary is required',
  [DocusignFieldTabLabel.beneficiaryRelationship]: 'Relationship to beneficiary is required',
  [DocusignFieldTabLabel.beneficiaryFirstName]: 'Beneficiary first name is required',
  [DocusignFieldTabLabel.beneficiaryLastName]: 'Beneficiary last name is required',
  [DocusignFieldTabLabel.beneficiaryMI]: 'Beneficiary middle initial is required',
  [DocusignFieldTabLabel.beneficiaryAddress]: 'Beneficiary address is required',
  [DocusignFieldTabLabel.beneficiaryDeathToEstateCheckbox]: 'Acknowledgement is required',
  [DocusignFieldTabLabel.insuranceCarrier]: 'Insurance Carrier is required',
  [DocusignFieldTabLabel.otherInsurancePlan]: 'Please chose other insurance plan option',
  [DocusignFieldTabLabel.eSignatureAck]: 'Please acknowledge eSignature',
  [DocusignFieldTabLabel.businessName]: 'Business entity name is required',
  [DocusignFieldTabLabel.ein]: 'Employer Identification Number (EIN) is required',
  [DocusignFieldTabLabel.vehicleYear]: 'Vehicle Year is required',
  [DocusignFieldTabLabel.vehicleMake]: 'Vehicle Make is required',
  [DocusignFieldTabLabel.vehicleModel]: 'Vehicle Model is required',
  [DocusignFieldTabLabel.vehicleVIN]: 'Vehicle VIN is required',
  [DocusignFieldTabLabel.vehicleLicensePlate]: 'Vehicle License Plate is required',
  [DocusignFieldTabLabel.gvw]: 'GVW is required',
  [DocusignFieldTabLabel.vehicleType]: 'Vehicle type is required',
  [DocusignFieldTabLabel.vehicleTypeOther]: 'Vehicle type is required',
  [DocusignFieldTabLabel.vehicleTypeRequired]: 'Vehicle type is required',
  [DocusignFieldTabLabel.vehicleTypeOtherRequired]: 'Vehicle type is required',
  [DocusignFieldTabLabel.vehicleYear2]: 'Additional Vehicle Year is required',
  [DocusignFieldTabLabel.vehicleMake2]: 'Additional Vehicle Make is required',
  [DocusignFieldTabLabel.vehicleModel2]: 'Additional Vehicle Model is required',
  [DocusignFieldTabLabel.vehicleVIN2]: 'Additional Vehicle VIN is required',
  [DocusignFieldTabLabel.vehicleLicensePlate2]: 'Additional Vehicle License Plate is required',
  [DocusignFieldTabLabel.gvw2]: 'GVW is required',
  [DocusignFieldTabLabel.vehicleType2]: 'Vehicle type is required',
  [DocusignFieldTabLabel.vehicleTypeOther2]: 'Vehicle type is required',
  [DocusignFieldTabLabel.vehicleYear3]: 'Additional Vehicle Year is required',
  [DocusignFieldTabLabel.vehicleMake3]: 'Additional Vehicle Make is required',
  [DocusignFieldTabLabel.vehicleModel3]: 'Additional Vehicle Model is required',
  [DocusignFieldTabLabel.vehicleVIN3]: 'Additional Vehicle VIN is required',
  [DocusignFieldTabLabel.vehicleLicensePlate3]: 'Additional Vehicle License Plate is required',
  [DocusignFieldTabLabel.gvw3]: 'GVW is required',
  [DocusignFieldTabLabel.vehicleType3]: 'Vehicle type is required',
  [DocusignFieldTabLabel.vehicleTypeOther3]: 'Vehicle type is required',
  [DocusignFieldTabLabel.taxClassificationType]: 'Tax classification type is required',
  [DocusignFieldTabLabel.llcType]: 'LLC type is required',
  [DocusignFieldTabLabel.exemptPayeeCode]: 'Exempt payee code is required',
  [DocusignFieldTabLabel.exemptionFatcaCode]: 'Exemption FATCA code is required',
  [DocusignFieldTabLabel.mmsOregonAddress]: 'MMS Oregon address is required',
  [DocusignFieldTabLabel.driverLicenseNumber]: 'Driver license number is required',
  [DocusignFieldTabLabel.driverLicenseState]: 'Driver license state is required',
  [DocusignFieldTabLabel.otherForeignPartners]: 'Please acknowledge other foreign partners',
  [DocusignFieldTabLabel.otherTaxClassification]: 'Other tax classification is required',
  [DocusignFieldTabLabel.inselAgreementCheckbox]: 'Acceptance of Membership Agreement is required',
  [DocusignFieldTabLabel.taxId]: 'Tax ID is required',
  [DocusignFieldTabLabel.companyTitle]: 'Company title is required',
  [DocusignFieldTabLabel.smartDeliveryCheckboxLease]: 'Acknowledgement is required',
  [DocusignFieldTabLabel.smartDeliveryCheckboxAdAck]: 'Acknowledgement is required',
  [DocusignFieldTabLabel.smartDeliveryCheckboxShirt]: 'Acknowledgement is required',
  [DocusignFieldTabLabel.smartDeliveryCheckboxIdBadge]: 'Acknowledgement is required',
  [DocusignFieldTabLabel.smartDeliveryCheckboxDecal]: 'Acknowledgement is required',
  [DocusignFieldTabLabel.addressSinceDate]: 'Current address since date is required',
  [DocusignFieldTabLabel.prevAddressSinceDate1]: 'Previous address since date is required',
  [DocusignFieldTabLabel.prevAddressSinceDate2]: 'Previous address since date is required',
  [DocusignFieldTabLabel.w9FullName]: 'Full name is required',
  [DocusignFieldTabLabel.w9BusinessName]: 'Business name is required',
  [DocusignFieldTabLabel.w9Address]: 'Address is required',
  [DocusignFieldTabLabel.w9City]: 'City is required',
  [DocusignFieldTabLabel.w9State]: 'State is required',
  [DocusignFieldTabLabel.w9County]: 'County is required',
  [DocusignFieldTabLabel.w9Zipcode]: 'ZIP code is required',
  [DocusignFieldTabLabel.w9Ssn]: 'Social security number is required',
  [DocusignFieldTabLabel.w9Ein]: 'Employer Identification Number (EIN) is required',
  [DocusignFieldTabLabel.bankName]: 'Bank name is required',
  [DocusignFieldTabLabel.bankRoutingNumber]: 'Routing number is required',
  [DocusignFieldTabLabel.bankAccountNumber]: 'Account number is required',
  [DocusignFieldTabLabel.bankAccountType]: 'Account type is required',
}

export const getValidationSchema = (fields: Array<DocusignSigningField>) => {
  const schemaObject: any = {}

  fields.forEach((item) => {
    if (!validationMap[item.tabLabel]) {
      return
    }

    schemaObject[item.tabLabel] = validationMap[item.tabLabel]

    if (item.required || item.tabLabel === DocusignFieldTabLabel.vehicleTypeRequired) {
      schemaObject[item.tabLabel] = schemaObject[item.tabLabel].required(
        requiredMap[item.tabLabel] || 'Required',
      )
    }
  })

  return Yup.object().shape(schemaObject, [
    [DocusignFieldTabLabel.w9FullName, DocusignFieldTabLabel.w9BusinessName],
    [DocusignFieldTabLabel.w9Ssn, DocusignFieldTabLabel.w9Ein],
  ])
}
